import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import map from 'lodash/map';
import reject from 'lodash/reject';
import sortBy from 'lodash/sortBy';
import { v4 } from 'uuid';

import {
  builtInTypes,
  scriptStepTypes,
  sortForVersionList,
} from '@xmatters/common/constants/scriptSteps';
import { AlertTriggerType } from 'xm-constants/triggerTypes';
import {
  TYPICAL_BLOCK_HEIGHT,
  BLOCK_ADD_BUTTON_HEIGHT,
  PORT_TYPES,
} from 'xm-constants/flowDesigner';
import { validateCallableFlowConnection } from 'utils/flow/callableTrigger';
import { getPopoverContainer } from 'utils/appLayout';
import { DEFAULT_TARGET_PORT_ID } from 'utils/defaultCanvasPortId';
import { IncidentTriggerOutputDescriptionPrefix } from 'xm-constants/incidentTriggers';
import { IntegrationOperation } from 'xm-constants/integration';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';

export const FLOW_POPOVER_ID = 'flow-designer-popover-container';
export const getFlowDesignerPortalNode = () =>
  document.querySelector(`#${FLOW_POPOVER_ID}`) || getPopoverContainer();

export const DEFAULT_NODE_DIMENSIONS = {
  w: 60,
  h: 60,
};
export const graphNodeToModelBlock = node => ({
  ...node.data.origin,
  id: node.id,
  label: node.text,
  presentation: {
    ...node.data.presentation,
    w: node.w,
    h: node.h,
  },
});

const isBasicPort = port => port === 'input' || port === 'output';
const isMergePort = port => port.length > 10; // HACK: Really: if it's a uuid

// Do not render nodes with a groupingBlockId; those must be
// manually rendered by their grouping/container node
export const shouldRenderBlock = block => block && !block.groupingBlockId;

export const prepareEdgeForJsPlumbNesting = edge => {
  const { to, from } = edge;

  const fromSuffix = isBasicPort(from.port) ? from.block.id : from.port;
  const toPort = to.port;

  let toSuffix;
  if (isBasicPort(toPort)) {
    toSuffix = to.block.id;
  } else if (isMergePort(toPort)) {
    toSuffix = DEFAULT_TARGET_PORT_ID;
  } else {
    toSuffix = toPort;
  }

  const toId =
    to.block.id === toSuffix ? to.block.id : `${to.block.id}.${toSuffix}`;
  const fromId =
    from.block.id === fromSuffix
      ? from.block.id
      : `${from.block.id}.${fromSuffix}`;

  return {
    ...edge,
    to: {
      ...to,
      block: {
        id: toId,
      },
    },
    from: {
      ...from,
      block: {
        id: fromId,
      },
    },
  };
};

export const graphEdgeToModelEdge = edge => {
  const getNode = obj => (obj.getNode ? obj.getNode() : obj);
  const { source, target } = edge;
  const sourceNode = getNode(source);
  const targetNode = getNode(target);

  const modelEdge = {
    ...edge.data,
    from: {
      block: {
        id: sourceNode.id,
      },
      port: source === sourceNode ? 'output' : source.id,
    },
    to: {
      block: {
        id: targetNode.id,
      },
      port: target === targetNode ? 'input' : target.id,
      // Hack: adding this to solve the problem where jsplumb sets the port to
      // `defaultTargetPort` and needs that value to correctly draw the new edge to
      // the step, while at the same time having the 'to.port' value be the uuid of the
      // source block so that outputs from the source block can be looked up for validation.
      sourceId: sourceNode.id,
    },
  };

  return prepareEdgeForJsPlumbNesting(modelEdge);
};

const triggerNodeTypesArray = [
  scriptStepTypes.OUTBOUND_WEBHOOK,
  scriptStepTypes.INBOUND_WEBHOOK,
  scriptStepTypes.BUILT_IN_HTTP,
  scriptStepTypes.CUSTOM_HTTP,
  scriptStepTypes.INBOUND_EMAIL,
  scriptStepTypes.FORM_TRIGGER,
  scriptStepTypes.AUTOMATION_TRIGGER,
  scriptStepTypes.CALLABLE_FLOW_RECEIVER,
  scriptStepTypes.EBFD_INCIDENT_RESPONSE_TRIGGER,
];

export const triggerNodeTypes = new Set(triggerNodeTypesArray);
export const isTriggerNodeType = type => triggerNodeTypes.has(type);

export const isCustomHttpIntegration = integration =>
  integration.type === 'CUSTOM_HTTP' ||
  (integration.type === 'INBOUND_WEBHOOK' &&
    integration.scriptStep !== undefined);

export const getIntegrationsToDelete = (block, integrations) => {
  let result;
  if (block.step.type === 'CUSTOM_HTTP') {
    // Can be multiple instances of this step type, do not delete all of them.
    result = find(integrations, { id: block.id });
    if (result) {
      // Only if we can find one.
      return [result];
    }
  }
  return filter(integrations, { id: block.step.id });
};

export const isToolStep = ({ type }) =>
  type === scriptStepTypes.SWITCH ||
  type === scriptStepTypes.CREATE_EVENT ||
  type === scriptStepTypes.MERGE ||
  type === scriptStepTypes.CREATE_ALERT;

// this is for the stuff we drag in from the RHS in connected steps I think
// we need to check that we can drag vars into
const titlePrefix = {
  FORM_TRIGGER: 'FORM_TRIGGER_ROW_TITLE',
  AUTOMATION_TRIGGER: 'FORM_TRIGGER_ROW_TITLE',
};

const descriptionPrefix = {
  INBOUND_EMAIL: 'OUTPUT_DESCRIPTION',
  FORM_TRIGGER: 'FORM_TRIGGER_ROW_DESC',
  AUTOMATION_TRIGGER: 'FORM_TRIGGER_ROW_DESC',
  ...IncidentTriggerOutputDescriptionPrefix,
};

export function getPropertyDescriptionKey(triggerType, prop, titleKey = false) {
  const outputKey = prop.replace(/\./g, '_').toUpperCase();
  const prefixMap = titleKey ? titlePrefix : descriptionPrefix;
  const prefix = prefixMap[triggerType] || triggerType;
  return `${prefix}_${outputKey}`;
}

export function getAllStepVersions(step) {
  if (!step) {
    return [];
  }
  return [step, ...step.versions].sort(sortForVersionList);
}

export const MAX_MERGE_INPUTS = 10;

export function validatePortConnection({
  allowedCallablesInFlow,
  blockMetadataMap,
  edges,
  sourceNodeId,
  sourcePortId,
  targetNodeId,
  ignoreMultipleOutgoingEdges = false,
}) {
  const sourcePortHasOutbound = !!edges.find(
    ({ from: { block, port } }) =>
      block.id.includes(sourceNodeId) &&
      (block.id.includes(sourcePortId) || port?.includes?.(sourcePortId)),
  );

  // prevent multiple outgoing edges from a port
  if (!ignoreMultipleOutgoingEdges && sourcePortHasOutbound) {
    return { isValid: false };
  }

  const sourcePortData = blockMetadataMap[sourcePortId];
  const sourceData = sourcePortData ?? blockMetadataMap[sourceNodeId];
  const targetData = blockMetadataMap[targetNodeId];

  // check if there is a path from the connection target node back to the source node
  // This is to prevent loops, for example if we have A -> B -> C and we want to connect
  // C -> A, check if there is a path A -> ... -> C
  if (sourceNodeId === targetNodeId) {
    return { isValid: false };
  }
  const createsCycle =
    sourceData &&
    // E.g.: connecting an ancestor block:
    (sourceData.allAncestorIds.has(targetNodeId) ||
      // E.g.: Create Alert or Initiate Incident subtrigger pointing back to own groupingBlock:
      sourceData.block.groupingBlockId === targetNodeId);
  if (createsCycle) {
    return { isValid: false };
  }

  // Prevent too many inputs to a single merge block
  if (
    targetData &&
    targetData.type === scriptStepTypes.MERGE &&
    targetData.parentIds.length >= MAX_MERGE_INPUTS
  ) {
    return {
      isValid: false,
      messageProps: { id: 'FLOW_DESIGN_MERGE_STEP_INPUT_FLOW_LIMIT' },
    };
  }

  const callableFlowsError =
    sourceData &&
    validateCallableFlowConnection({
      allowedCallablesInFlow,
      blockMetadataMap,
      sourceId: sourceData.id,
      targetId: targetNodeId,
    });
  if (callableFlowsError) {
    return {
      isValid: false,
      messageProps: callableFlowsError,
    };
  }

  return { isValid: true };
}

export function removeEdgesFromBlockPort(edges, blockId, portId) {
  // JSPlumb uses this as the from id:
  const combinedId = `${blockId}.${portId}`;
  const isEdgeFromTheBlockPort = ({ from: { port, block } }) =>
    port === portId && (block.id === blockId || block.id === combinedId);
  return reject(edges, isEdgeFromTheBlockPort);
}

/**
 * NOTE: getDefaultFlowTriggerIntegrationData returns just the default, common data;
 *       some additional data or changes may be necessary based on the type.
 *       E.g. "OUTBOUND_WEBHOOK" requires a triggeredBy value, others cannot have it.
 */
export function getDefaultFlowTriggerIntegrationData({
  planId,
  uniqueName,
  type, // e.g. "OUTBOUND_WEBHOOK"
  id,
}) {
  const newIntegrationId = id || v4();
  return {
    id: newIntegrationId,
    deployed: true,
    environment: 'HOSTED',
    plan: { id: planId },
    // Any two integrations are not allowed to have the same name in a comm plan.
    // This helps prevent that by adding an incrementing number.
    name: `${uniqueName} - ${newIntegrationId}`,
    operation: IntegrationOperation.RUN_SCRIPT,
    createdBy: 'FLOW',
    type,
    integrationType: type,
    agents: [],
  };
}

/*
 * Used because across our validations and similar we rely on `scriptStepInput` value, because it holds both
 * A) The identifier (scriptStepInput.id)
 * B) Data our forms will use to render the inputs
 * However API is more correct: for formPropertyInputs it only has `formSectionItem` as its reference.
 *
 * Easiest place to achieve consistency for now is at the ends (on fetch, and before save)
 * Not super nice modelling, but it's what we have. TODO: try to iron out UI side to be equally correct.
 */
export const prepareApiInputs = inputs =>
  map(inputs && inputs.data, input => {
    const { formSectionItem, mergeOutput, builtInInput } = input;
    if (formSectionItem && formSectionItem.id) {
      return {
        ...input,
        scriptStepInput: { ...formSectionItem },
      };
    }
    if (mergeOutput) {
      return {
        ...input,
        scriptStepInput: {
          ...mergeOutput,
          required: true,
        },
      };
    }
    if (builtInInput) {
      return {
        ...input,
        scriptStepInput: { ...builtInInput, isBuiltInInput: true },
      };
    }
    return input;
  }).sort((a, b) => a.scriptStepInput?.position - b.scriptStepInput?.position);

export const injectSwitchCases = planCanvas => {
  const switchCasesByBlockId = groupBy(planCanvas.switchCases, 'block.id');

  // eslint-disable-next-line no-param-reassign
  planCanvas.blocks = planCanvas.blocks.map(block => {
    const cases = switchCasesByBlockId[block.id];
    if (cases) {
      const ports = sortBy(cases, 'orderNum').map((switchCase, i) => ({
        ...switchCase,
        // Ensure orderNum has no duplicate values, no gaps, and represents the correct indices:
        orderNum: i + 1,
      }));

      return { ...block, ports };
    }
    return block;
  });
};

export const injectConditionPaths = (
  planCanvas,
  existingPortsByBlockId = {},
) => {
  const { conditionPathsByBlockId } = planCanvas;
  const existingPortMapByBlockId = mapValues(existingPortsByBlockId, ports =>
    keyBy(ports, 'id'),
  );

  // eslint-disable-next-line no-param-reassign
  planCanvas.blocks = planCanvas.blocks.map(block => {
    const conditionPaths = conditionPathsByBlockId[block.id];
    if (conditionPaths) {
      const existingPortMap = existingPortMapByBlockId[block.id] ?? {};
      const ports = conditionPaths.map(path => ({
        ...(existingPortMap[path.id] ?? {}),
        id: path.id,
        jsPlumbId: path.id,
      }));
      ports.push({
        ...(existingPortMap.defaultSourcePort ?? {}),
        id: 'defaultSourcePort',
      });

      return { ...block, ports };
    }
    return block;
  });
};

export const clearBlockPorts = canvas => {
  if (!canvas.blocks) {
    return;
  }

  // eslint-disable-next-line no-param-reassign
  canvas.blocks = canvas.blocks.map(block => {
    if (block.ports) {
      const { ports, ...rest } = block;
      return rest;
    }
    return block;
  });
};

export const injectGenericSubtriggers = ({
  blocks,
  edges,
  existingPortsByBlockId = {},
}) => {
  const blocksById = keyBy(blocks, 'id');
  const outgoingEdgesByBlockId = keyBy(edges, 'from.block.id');
  const existingPortMapByBlockId = mapValues(existingPortsByBlockId, ports =>
    keyBy(ports, 'id'),
  );
  blocks.forEach(block => {
    if (
      block.groupingBlockId &&
      (block?.step?.type === 'INTEGRATION' ||
        block?.step?.type === 'INBOUND_WEBHOOK')
    ) {
      const groupingBlock = blocksById[block.groupingBlockId];
      if (!groupingBlock) {
        return;
      }
      const existingPortMap = existingPortMapByBlockId[groupingBlock.id] ?? {};
      if (!groupingBlock.ports) {
        groupingBlock.ports = [
          {
            ...(existingPortMap.defaultSourcePort ?? {}),
            id: 'defaultSourcePort',
          },
        ];
      }
      groupingBlock.ports.push({
        ...(existingPortMap[block.id] ?? {}),
        id: block.id,
        triggerBlockId: block.id, // Used by validation
        jsPlumbId: block.id, // Used in jsplumb
        portType: 'pseudoBlockOutput',
      });

      /**
       * Update edge references so that JSPlumb correctly draws them from the sub-trigger block's ports.
       * The backend model will point the edges from the `triggerBlockId` => the target,
       * JSPlumb needs the source reference to be `${groupingBlockId}.${triggerBlockId}`.
       */
      const outgoingEdge = outgoingEdgesByBlockId[block.id];
      if (outgoingEdge) {
        outgoingEdge.from.port = block.id;
        outgoingEdge.from.block.id = `${groupingBlock.id}.${block.id}`; // JSPlumb format
      }
    }
  });
};

export const modelBlockToGraphNode = block => {
  const node = {
    id: block.id,
    text: block.label,
    type: block.step.type, // BuildCanvas in reducer already mapped type to the proper type
    data: {
      label: block.label,
      origin: block,
    },
    w: get(block, 'presentation.w', 100),
    h: get(block, 'presentation.h', 50),
    isDuplicateHighlightVisible: block.isDuplicateHighlightVisible,
    ports: block.ports,
    groupingBlockId: block.groupingBlockId,
  };

  const x = get(block, 'presentation.x');
  const y = get(block, 'presentation.y');

  if (x != null && y != null) {
    node.left = x;
    node.top = y;
    node.locked = true;
  }

  node.stepId = block.step.id;

  return node;
};

export const modelEdgeToGraphEdge = edge => ({
  data: {
    origin: { ...edge },
  },
  source: edge.from.block.id,
  target: edge.to.block.id,
});

const getNodeType = step => {
  if (step.id === 'RESPONSES') {
    return 'RESPONSES';
  }
  return step.originalBuiltInType === builtInTypes.XMATTERS_CREATE_INCIDENT
    ? builtInTypes.XMATTERS_CREATE_INCIDENT
    : step.integrationType;
};

export const getNodeInfo = step => {
  const type = getNodeType(step);
  const isTrigger = isTriggerNodeType(type);
  const triggerBy = isTrigger ? step.id : null;

  return {
    icon: step.icon,
    id: v4(),
    isTrigger,
    isResponse: type === 'RESPONSES',
    label: step.name,
    triggerBy,
    type,
    stepId: step.uuid,
    triggerAuthType: step.triggerAuthType,
    environment: step.allowedEnvironment,
  };
};

export const edgeOriginatesFromBlockId = (edge, blockId) =>
  edge.from.block.id.startsWith(blockId) || edge.from.port === blockId;

export const edgePointsToBlockId = (edge, blockId) =>
  edge.to.block.id.startsWith(blockId) || edge.to.port === blockId;

export const edgeConnectsBlockId = (edge, blockId) =>
  edgeOriginatesFromBlockId(edge, blockId) ||
  edgePointsToBlockId(edge, blockId);

export const shouldWarnBeforeDeletingBlock = (block, edges) => {
  const { id, step } = block;
  const typeAlwaysRequiresWarning = ![
    scriptStepTypes.CREATE_EVENT,
    scriptStepTypes.CREATE_ALERT,
    scriptStepTypes.SCRIPT_STEP,
    builtInTypes.XMATTERS_CREATE_INCIDENT,
  ].includes(step?.type);

  return (
    typeAlwaysRequiresWarning ||
    find(edges, edge => edgeConnectsBlockId(edge, id))
  );
};

export function getMoveBlocksBelowPositions({
  bottom: bottomProp,
  canvas,
  leftBoundary,
  shiftDistance,
}) {
  // the -40 magic number is because some nodes look visually below even when they _technically_ are not:
  const BOTTOM_OFFSET = -40;
  // 160 is a reasonable left boundary clearance where nodes should not need to be repositioned vertically
  // It does not appear reasonable to make assumptions on the right boundary.
  // A counter-example would be an existing sub-trigger with a long chain of steps. All steps in that chain should get shifted together.
  const LEFT_OFFSET = 160;
  const bottom = bottomProp + BOTTOM_OFFSET;
  const left = leftBoundary - LEFT_OFFSET;

  const originalPositions = canvas.blocks
    .filter(block => shouldRenderBlock(block))
    .map(({ id, presentation }) => ({
      x: presentation?.x ?? 0,
      y: presentation?.y ?? 0,
      id,
    }));

  return originalPositions
    .filter(item => item.y > bottom && item.x > left)
    .map(item => ({ ...item, y: item.y + shiftDistance }));
}

const isVisibleResponsePort = port =>
  !port.hidden && port.triggerType === AlertTriggerType.RESPONSE;
// This method is not perfect, some nodes have a little extra from some UI (e.g. buttons underneath).
// It exists to simplify code elsewhere, but getting real, measured heights may be more ideal in some cases.
export const approximateBlockHeight = block => {
  if (!block) {
    return 0;
  }
  // Block.ports tends to mean _extra_ ports, so Switch Cases, Subtriggers, etc.
  if (block.ports?.length) {
    const subNodeCount = block.ports?.length ?? 0;
    let extraHeight = BLOCK_ADD_BUTTON_HEIGHT;
    if (block.step?.type === scriptStepTypes.SWITCH) {
      // Switch have an additional "default case", which doesn't get a proper
      // "port" for measuring and is slightly shorter than normal blocks:
      extraHeight += 100;
    } else if (block.ports.find(isVisibleResponsePort)) {
      // Responses get an extra add button:
      extraHeight += BLOCK_ADD_BUTTON_HEIGHT;
    }

    return (subNodeCount + 1) * TYPICAL_BLOCK_HEIGHT + extraHeight;
  }
  return TYPICAL_BLOCK_HEIGHT;
};

export const isOutboundPortType = port =>
  port === PORT_TYPES.RESPONSE ||
  port === PORT_TYPES.SUBTRIGGER ||
  port === PORT_TYPES.SOURCE;

export const isTemplateStepType = type => type && type.startsWith('TEMPLATE_');
export const getAssociatedTemplateStepType = type => `TEMPLATE_${type}`;
